import {
  Alert,
  AlertDescription,
  AlertTitle,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';

import { FieldGrid, FieldGridItem, RecordSection, RichText } from '@/design';
import { formatDateTime } from '@/domains/survey';
import { PAGE_SIZE_DEFAULT, type IPage } from '../../api/oversight-contracts';
import type {
  IAdminParticipantDetail,
  IAdminSalesforceObject,
  IAdminSalesforceRecord,
  IAdminSalesforceRecordField
} from '../../api/participant-contracts';
import { participantsApi } from '../../api/participants-api';
import {
  LoadError,
  Pager,
  PastTheEnd,
  Section,
  TableSkeleton,
  useAdminData
} from '../../components/admin-page';
import { formatDay } from '../../components/days';
import { asWritten, formatMoney, yesNo } from '../../components/sec-adv-values';
import { TableFrame } from '../../components/table-frame';
import { refusalMessage } from '../../refusal';
import { MemberPart } from './household';
import { useEmptyParts } from './empty-parts';
import { HUB_MEMORY_PAGE } from './hub-memory';

/**
 * The hub's **Records** (the advisor record page's S4, block F3) — what survey-api archived from
 * Salesforce for each member: the survey record, the `Lead`, the survey history, `Note`, `Task`
 * and the rest of the reach, over `GET …/participants/{id}/salesforce-records`. Read-only and
 * verbatim, under the labels Salesforce kept, with **no badge or chip saying where it came from**
 * (the backend's D11): it is a section like any other.
 *
 * **Always drawn** (F3's R2), unlike the roadmap's "no section for `[]`": whether a member's
 * archive holds anything takes a read, and the hub document carries nothing about it — the
 * Salesforce tasks' precedent. A nav that grew once every member's read had landed was the
 * alternative, rejected. A member whose list is empty says so.
 *
 * **Per member, its own list read below a loaded hub**, with the three arms, **read once and never
 * re-read** — not on the poll and not on `written`: the archive changes only when the ETL runs, and
 * nothing on this page writes to it. The objects come in survey-api's order, each one a fold
 * titled as Salesforce titles a related list, "Survey History (13)" — nothing here sorts, counts or
 * drops what was sent.
 *
 * **An object's page is read when its fold first opens** (R4, `RecordSection`'s `lazy`), by any
 * way of opening it: without it every object of every member would read its first page on load,
 * `Survey__c`'s ~375 fields among them. The price is that find-in-page does not reach a fold never
 * opened. Each page read has the admin lists' rules (R6): a record is a screenful, so a grid
 * object is read 10 a page, a history at the default; a refusal is said in survey-api's words
 * (`refusalMessage`), never as "nothing here"; a later page that fails leaves the last one drawn.
 *
 * **A history object is drawn as Salesforce draws it** (the backend's D10): its related list, one
 * row per change — Date, Field, User, Original value, New value, and the record changed only where
 * the page holds more than one. Its `Field` is named by survey-api's `fieldLabel`, the API name
 * beside it; the API name alone where no label came. Every other object is its records, each a
 * grid of its fields. `Task` is drawn like any other object, with one sentence pointing at the
 * Salesforce tasks section, which draws the same rows by the disclosures they name. An object
 * survey-api holds no describe of is paged all the same, its fields the record's own members each
 * labelled by its name — drawn once, as any label is: the API name is the wrapper's hook, never
 * text beside the label.
 *
 * **A value is drawn by its Salesforce type** (F3's Q2, ruled (a)), the SEC ADV register's
 * `CatalogueValue` precedent — display, not validation: a boolean as Yes / No, a currency as
 * money, a percent with its sign (Salesforce's percent is 0–100), a date as the day, a datetime as
 * the instant, a textarea through `RichText` always (U15 — the archive cannot tell markup from
 * plain text, and the sanitiser draws both), a reference as the name the archive holds for it
 * beside its id; anything else, and anything whose JSON kind does not fit its type, **as written**.
 * Every value as written was the alternative, rejected: `true` and `2016-05-04T13:22:11.000+0000`
 * are verbatim and unlike the Lightning page staff know. Nothing is refused or flagged. An id —
 * a record's or a reference's — is text and never a link: no Salesforce address is configured, and
 * Salesforce is being switched off.
 */

/** Records per page of an object drawn as a grid: one record is a screenful. */
const GRID_PAGE_SIZE = 10;

/**
 * The fields of Salesforce's `*__History` objects a history row is drawn by — a mirror of that
 * object's shape, the `COLUMN_DEFAULTS` precedent: the related list's columns, and `ParentId`, the
 * record a change was made on. A field a row carries outside them is drawn under the new value,
 * never dropped.
 */
const HISTORY_COLUMNS = {
  date: 'CreatedDate',
  field: 'Field',
  user: 'CreatedById',
  original: 'OldValue',
  changed: 'NewValue',
  record: 'ParentId'
} as const;

const HISTORY_FIELDS: ReadonlySet<string> = new Set(Object.values(HISTORY_COLUMNS));

/** The `Field` Salesforce writes on the history row of a record's creation. */
const CREATED_MARKER = 'created';

/** Whether an object is a field history — survey-api's own test for `fieldLabel`. */
function isHistory(sobject: string): boolean {
  return sobject.endsWith('__History');
}

/**
 * The object a custom object's field history records changes to — Salesforce's naming, `Foo__c`'s
 * history being `Foo__History`; a mirror of that rule, which every history in the archive's reach
 * follows.
 */
function historyParent(sobject: string): string {
  return `${sobject.slice(0, -'__History'.length)}__c`;
}

export function RecordsSection({
  members,
  anchor
}: {
  members: IAdminParticipantDetail[];
  anchor: string;
}) {
  const multi = members.length > 1;
  const { empty, say } = useEmptyParts(members.map((member) => member.participantId));
  return (
    <Section
      title="Records"
      anchor={anchor}
      empty={empty}
      description="Read-only. Each object and each field carries the label it was kept under."
    >
      <CardContent className="grid gap-8">
        {members.map((member, index) => (
          <MemberPart
            key={member.participantId}
            member={member}
            opened={index === 0}
            multi={multi}
          >
            <MemberRecords participantId={member.participantId} onCount={say} />
          </MemberPart>
        ))}
      </CardContent>
    </Section>
  );
}

function MemberRecords({
  participantId,
  onCount
}: {
  participantId: string;
  /** Says how many objects the archive holds for the member, as the read lands. */
  onCount: (participantId: string, count: number) => void;
}) {
  const { data, error } = useAdminData(async (signal) => {
    let objects: IAdminSalesforceObject[];
    try {
      objects = await participantsApi.listSalesforceObjects(participantId, signal);
    } catch (listError) {
      throw new Error(refusalMessage(listError, 'Could not read the records'));
    }
    // A 2xx that is not the list — an empty body, which the client reads as `null` — is a failed
    // read, said; kept, it would hold the part on its skeleton for good.
    if (!Array.isArray(objects)) throw new Error('Could not read the records');
    return objects;
  }, [participantId]);

  const count = data?.length;
  React.useEffect(() => {
    if (count !== undefined) onCount(participantId, count);
  }, [participantId, count, onCount]);

  if (data === null) return error ? <LoadError message={error} /> : <TableSkeleton rows={2} />;
  if (data.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No Salesforce record reaches this participant.
      </p>
    );
  }
  return (
    <div className="grid">
      {data.map((object) => (
        <ArchivedObject
          key={object.sobject}
          participantId={participantId}
          object={object}
          parents={
            isHistory(object.sobject)
              ? data.find((parent) => parent.sobject === historyParent(object.sobject))?.count
              : undefined
          }
        />
      ))}
    </div>
  );
}

/** One object of the archive: a fold, read on its first opening. */
function ArchivedObject({
  participantId,
  object,
  parents
}: {
  participantId: string;
  object: IAdminSalesforceObject;
  /** A history's: how many records of the object it records changes to reach the member. */
  parents?: number;
}) {
  return (
    <RecordSection
      page={HUB_MEMORY_PAGE}
      sectionKey={`records.${object.sobject}`}
      title={`${object.label} (${object.count})`}
      level={3}
      defaultOpen={false}
      filled={0}
      total={0}
      lazy
    >
      <div className="grid gap-4">
        {object.sobject === 'Task' && (
          <p className="text-sm text-muted-foreground">
            The same tasks, with the disclosures they name, are under Salesforce tasks.
          </p>
        )}
        <ObjectPage participantId={participantId} object={object} parents={parents} />
      </div>
    </RecordSection>
  );
}

function ObjectPage({
  participantId,
  object: { sobject, label },
  parents
}: {
  participantId: string;
  object: IAdminSalesforceObject;
  parents?: number;
}) {
  const history = isHistory(sobject);
  const pageSize = history ? PAGE_SIZE_DEFAULT : GRID_PAGE_SIZE;
  const [page, setPage] = React.useState(1);
  const { data, error, reload } = useAdminData(
    async (signal) => {
      let read: IPage<IAdminSalesforceRecord>;
      try {
        read = await participantsApi.listSalesforceRecords(
          participantId,
          sobject,
          { page, pageSize },
          signal
        );
      } catch (pageError) {
        // survey-api's `problems[]` say what was refused — an object outside the archive's reach,
        // a page out of bounds — and its title does not.
        throw new Error(refusalMessage(pageError, `Could not read ${label}`));
      }
      // A 2xx that is not a page (an empty body, the list's shape) is a failed read, said — never
      // a skeleton for good, nor a hub that throws from render.
      if (!Array.isArray(read?.items)) throw new Error(`Could not read ${label}`);
      return read;
    },
    [participantId, sobject, page, pageSize]
  );
  // The activity's rule: the pager is drawn from the page last read, so after a failed read of
  // page 2 its "Next" is for page 2 again — `page` already, which setting would not ask for.
  const turnTo = (target: number) => (target === page ? void reload() : setPage(target));

  return (
    <div className="grid gap-4">
      {error &&
        (data === null ? (
          <LoadError message={error} />
        ) : (
          <Alert variant="destructive">
            <AlertTitle>Could not refresh {label} — showing it as last read</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ))}
      {data === null ? (
        error ? null : <TableSkeleton rows={2} />
      ) : (
        <>
          {data.items.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {data.total > 0 ? (
                <PastTheEnd
                  page={data.page}
                  pageSize={data.pageSize}
                  total={data.total}
                  counted={['record', 'records']}
                  onPage={turnTo}
                />
              ) : (
                `No ${label} record reaches this participant.`
              )}
            </p>
          ) : history ? (
            <HistoryTable label={label} records={data.items} parents={parents} />
          ) : (
            data.items.map((record) => <ArchivedRecord key={record.sfId} record={record} />)
          )}
          <Pager
            page={data.page}
            pageSize={data.pageSize}
            total={data.total}
            onPage={turnTo}
            quiet
          />
        </>
      )}
    </div>
  );
}

/** One record: when it last changed and its Salesforce id, then its fields in the order sent,
 *  label over value. Every field sent holds a value, so the grid's "Show empty fields" never
 *  appears. */
function ArchivedRecord({ record }: { record: IAdminSalesforceRecord }) {
  return (
    <div data-sf-id={record.sfId} className="grid gap-3 rounded-md border border-border p-4">
      <h4 className="flex flex-wrap gap-x-3 text-xs text-muted-foreground">
        <span>{formatDateTime(record.modifiedAt)}</span>
        <code className="select-all">{record.sfId}</code>
      </h4>
      <FieldGrid>
        {record.fields.map((field) => (
          // The kit's item takes no hook of its own; the wrapper carries the API name, since two
          // fields can share a label, and draws nothing itself.
          <div key={field.name} data-sf-field={field.name} className="contents">
            <FieldGridItem label={field.label} filled wide={field.type === 'textarea'}>
              <ArchivedValue field={field} />
            </FieldGridItem>
          </div>
        ))}
      </FieldGrid>
    </div>
  );
}

/** A page of a history object as Salesforce's related list draws it, a row per change. */
function HistoryTable({
  label,
  records,
  parents
}: {
  label: string;
  records: IAdminSalesforceRecord[];
  parents?: number;
}) {
  // The record column says which record a change was made on, and only a member with more than
  // one record of the object changed — a team's, or a person with two survey records — needs it.
  // Decided by the object list's count, not by the page: a page holding one record's changes would
  // otherwise draw the older record's as the current one's (F3's gate 2, ruled 2026-10-03). The
  // page's own parents decide only where the list names no such object.
  const byRecord =
    parents !== undefined
      ? parents > 1
      : new Set(
          records.flatMap((record) => {
            const parent = fieldOf(record, HISTORY_COLUMNS.record);
            return parent === undefined ? [] : [asWritten(parent.value)];
          })
        ).size > 1;
  return (
    <TableFrame>
      <Table aria-label={label}>
        <TableHeader>
          <TableRow>
            <TableHead>Date</TableHead>
            <TableHead>Field</TableHead>
            <TableHead>User</TableHead>
            <TableHead>Original value</TableHead>
            <TableHead>New value</TableHead>
            {byRecord && <TableHead>Record</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {records.map((record) => (
            <TableRow key={record.sfId} data-sf-id={record.sfId} className="align-top">
              <TableCell className="text-xs whitespace-nowrap text-muted-foreground">
                <HistoryValue record={record} name={HISTORY_COLUMNS.date} />
              </TableCell>
              <TableCell>
                <ChangedField record={record} />
              </TableCell>
              <TableCell>
                <HistoryValue record={record} name={HISTORY_COLUMNS.user} />
              </TableCell>
              <TableCell className="wrap-anywhere">
                <HistoryValue record={record} name={HISTORY_COLUMNS.original} />
              </TableCell>
              <TableCell className="wrap-anywhere">
                <div className="grid gap-1">
                  <span>
                    <HistoryValue record={record} name={HISTORY_COLUMNS.changed} />
                  </span>
                  {record.fields
                    .filter((field) => !HISTORY_FIELDS.has(field.name))
                    .map((field) => (
                      <span
                        key={field.name}
                        data-sf-field={field.name}
                        className="text-xs text-muted-foreground"
                      >
                        {field.label}: <ArchivedValue field={field} />
                      </span>
                    ))}
                </div>
              </TableCell>
              {byRecord && (
                <TableCell>
                  <HistoryValue record={record} name={HISTORY_COLUMNS.record} />
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableFrame>
  );
}

function fieldOf(
  record: IAdminSalesforceRecord,
  name: string
): IAdminSalesforceRecordField | undefined {
  return record.fields.find((field) => field.name === name);
}

/** One of a history row's own fields by its type, or "—" where the row has none: survey-api sends
 *  only the fields that hold a value. */
function HistoryValue({ record, name }: { record: IAdminSalesforceRecord; name: string }) {
  const field = fieldOf(record, name);
  return field === undefined ? '—' : <ArchivedValue field={field} />;
}

/** The field a change was made to: survey-api's `fieldLabel` with the API name beside it, the API
 *  name alone where no label came (null, or an API older than the member), and a creation in
 *  Salesforce's own words. */
function ChangedField({ record }: { record: IAdminSalesforceRecord }) {
  const field = fieldOf(record, HISTORY_COLUMNS.field);
  if (field === undefined) return '—';
  const name = asWritten(field.value);
  if (name === CREATED_MARKER) return 'Created.';
  const code = <code className="text-xs text-muted-foreground">{name}</code>;
  if (record.fieldLabel === undefined || record.fieldLabel === null) return code;
  return (
    <>
      {record.fieldLabel} {code}
    </>
  );
}

/** One field's value by its Salesforce type — or as written where the value is not of it. */
function ArchivedValue({ field }: { field: IAdminSalesforceRecordField }): React.ReactNode {
  const { type, value } = field;
  const written = asWritten(value);
  switch (type) {
    case 'boolean':
      return typeof value === 'boolean' ? yesNo(value) : written;
    case 'currency':
      return typeof value === 'number' ? formatMoney(value) : written;
    case 'percent':
      return typeof value === 'number' ? `${value}%` : written;
    case 'date':
      return typeof value === 'string' ? formatDay(value) : written;
    case 'datetime':
      return typeof value === 'string' ? formatSalesforceInstant(value) : written;
    case 'textarea':
      return typeof value === 'string' ? <RichText html={value} /> : written;
    case 'reference':
      return typeof value === 'string' ? <Reference id={value} name={field.refName} /> : written;
    default:
      return written;
  }
}

/** A reference: the name of the record it points at, where the archive holds that record, with
 *  its id beside — or the id alone. Text, never a link. */
function Reference({ id, name }: { id: string; name: string | undefined }) {
  const code = <code className="text-xs text-muted-foreground select-all">{id}</code>;
  if (name === undefined) return code;
  return (
    <>
      {name} {code}
    </>
  );
}

/**
 * Salesforce's datetime (`2016-05-04T13:22:11.000+0000`) as the instant it names. Its offset has
 * no colon, which ISO 8601's extended form — the one `Date` is specified to read — requires; V8
 * reads it anyway, other engines need not. So the colon is supplied before parsing, and a value
 * that still does not read as an instant is drawn as written. Only a date with a time is parsed:
 * `Date.parse` also takes a bare day (as UTC midnight — the evening before, west of Greenwich) and
 * strings like `May 4`, which would draw an instant nobody wrote.
 */
function formatSalesforceInstant(value: string): string {
  if (!/^\d{4}-\d{2}-\d{2}T/.test(value)) return value;
  const iso = value.replace(/([+-]\d{2})(\d{2})$/, '$1:$2');
  return Number.isNaN(Date.parse(iso)) ? value : formatDateTime(iso);
}
